import { useCallback, useMemo } from "react";

import { SALE_STATUS as _SALE_STATUS } from "../constants";

import type { FilterInSaleAndStopSellingProps, InSaleAndStopSellingItemsType } from "../../../type";
import type { CheckedState } from "@radix-ui/react-checkbox";

type UseFilterCheckboxStateProps = {
	itemsName: FilterInSaleAndStopSellingProps["itemsName"];
	selectedItemIDs: string[];
	onChangeSelectedItemIDs: (ids: string[]) => void;
	inSaleItems: InSaleAndStopSellingItemsType;
	stopSellingItems: InSaleAndStopSellingItemsType;
};

export const useFilterCheckboxState = ({
	itemsName,
	selectedItemIDs,
	onChangeSelectedItemIDs,
	inSaleItems,
	stopSellingItems,
}: UseFilterCheckboxStateProps) => {
	const SALE_STATUS = useMemo(() => {
		return _SALE_STATUS(itemsName);
	}, [itemsName]);

	const addedSelectAllInSaleItems = useMemo(() => {
		return [SALE_STATUS.IN_SALE.SELECT_ALL_ITEM, ...inSaleItems];
	}, [SALE_STATUS.IN_SALE.SELECT_ALL_ITEM, inSaleItems]);

	const addedSelectAllStopSellingItems = useMemo(() => {
		return [SALE_STATUS.STOP_SELLING.SELECT_ALL_ITEM, ...stopSellingItems];
	}, [SALE_STATUS.STOP_SELLING.SELECT_ALL_ITEM, stopSellingItems]);

	const handleSelectAllInSaleItems = useCallback(() => {
		onChangeSelectedItemIDs([
			...addedSelectAllInSaleItems.map((item) => item.id),
			...selectedItemIDs.filter((value) => !addedSelectAllInSaleItems.map((item) => item.id).includes(value)),
		]);
	}, [addedSelectAllInSaleItems, onChangeSelectedItemIDs, selectedItemIDs]);

	const handleUnselectAllInSaleItems = useCallback(() => {
		onChangeSelectedItemIDs(
			selectedItemIDs.filter((value) => !addedSelectAllInSaleItems.map((item) => item.id).includes(value)),
		);
	}, [addedSelectAllInSaleItems, onChangeSelectedItemIDs, selectedItemIDs]);

	const handleSelectAllStopSellingItems = useCallback(() => {
		onChangeSelectedItemIDs([
			...addedSelectAllStopSellingItems.map((item) => item.id),
			...selectedItemIDs.filter(
				(value) => !addedSelectAllStopSellingItems.map((item) => item.id).includes(value),
			),
		]);
	}, [addedSelectAllStopSellingItems, onChangeSelectedItemIDs, selectedItemIDs]);

	const handleUnselectAllStopSellingItems = useCallback(() => {
		onChangeSelectedItemIDs(
			selectedItemIDs.filter((value) => !addedSelectAllStopSellingItems.map((item) => item.id).includes(value)),
		);
	}, [addedSelectAllStopSellingItems, onChangeSelectedItemIDs, selectedItemIDs]);

	const isAllInSaleItemsSelected = useCallback(
		(currentSelectedIds: string[]) => {
			return inSaleItems.every((item) => currentSelectedIds.includes(item.id));
		},
		[inSaleItems],
	);

	const isAllStopSellingItemsSelected = useCallback(
		(currentSelectedIds: string[]) => {
			return stopSellingItems.every((item) => currentSelectedIds.includes(item.id));
		},
		[stopSellingItems],
	);

	const handleCheckboxChange = useCallback(
		({ checked, id }: { checked: CheckedState; id: string }) => {
			if (id === SALE_STATUS.IN_SALE.SELECT_ALL_ID) {
				checked ? handleSelectAllInSaleItems() : handleUnselectAllInSaleItems();
				return;
			}

			if (id === SALE_STATUS.STOP_SELLING.SELECT_ALL_ID) {
				checked ? handleSelectAllStopSellingItems() : handleUnselectAllStopSellingItems();
				return;
			}

			let newSelectedIds: string[];

			if (checked) {
				newSelectedIds = [
					...selectedItemIDs.filter(
						(value) =>
							value !== SALE_STATUS.STOP_SELLING.SELECT_ALL_ID &&
							value !== SALE_STATUS.IN_SALE.SELECT_ALL_ID,
					),
					id,
				];

				// 全てのinSaleアイテムが選択されている場合、SELECT_ALL_IDも追加
				if (isAllInSaleItemsSelected(newSelectedIds)) {
					newSelectedIds.push(SALE_STATUS.IN_SALE.SELECT_ALL_ID);
				}

				// 全てのstopSellingアイテムが選択されている場合、SELECT_ALL_IDも追加
				if (isAllStopSellingItemsSelected(newSelectedIds)) {
					newSelectedIds.push(SALE_STATUS.STOP_SELLING.SELECT_ALL_ID);
				}
			} else {
				newSelectedIds = selectedItemIDs?.filter(
					(value) =>
						value !== id &&
						value !== SALE_STATUS.STOP_SELLING.SELECT_ALL_ID &&
						value !== SALE_STATUS.IN_SALE.SELECT_ALL_ID,
				);
			}

			onChangeSelectedItemIDs(newSelectedIds);
		},
		[
			SALE_STATUS.IN_SALE.SELECT_ALL_ID,
			SALE_STATUS.STOP_SELLING.SELECT_ALL_ID,
			handleSelectAllInSaleItems,
			handleSelectAllStopSellingItems,
			handleUnselectAllInSaleItems,
			handleUnselectAllStopSellingItems,
			isAllInSaleItemsSelected,
			isAllStopSellingItemsSelected,
			onChangeSelectedItemIDs,
			selectedItemIDs,
		],
	);

	const addedSelectAllItemSelectedIDs = useMemo(() => {
		const copy = [...selectedItemIDs];

		if (isAllInSaleItemsSelected(copy)) {
			copy.push(SALE_STATUS.IN_SALE.SELECT_ALL_ID);
		}

		if (isAllStopSellingItemsSelected(copy)) {
			copy.push(SALE_STATUS.STOP_SELLING.SELECT_ALL_ID);
		}

		return copy;
	}, [
		SALE_STATUS.IN_SALE.SELECT_ALL_ID,
		SALE_STATUS.STOP_SELLING.SELECT_ALL_ID,
		isAllInSaleItemsSelected,
		isAllStopSellingItemsSelected,
		selectedItemIDs,
	]);

	return {
		addedSelectAllItemSelectedIDs,
		addedSelectAllInSaleItems,
		addedSelectAllStopSellingItems,
		handleCheckboxChange,
	};
};
